import { ApiUrl } from '@config/apiUrl';
import { api } from '@lib/api/apiClient';
import { logError } from '@lib/logError';
import { queryClient } from '@lib/queryClient';

export const FEATURE_FLAG_KEYS = {
  CLICK_INS: 'CLICK_INS',
  NON_AUTOMOTIVE_CATEGORY: 'NON_AUTOMOTIVE_CATEGORY',
} as const;

export type FeatureFlagKey =
  (typeof FEATURE_FLAG_KEYS)[keyof typeof FEATURE_FLAG_KEYS];

export type FeatureFlagsMap = Record<string, boolean>;

export const DEFAULT_FEATURE_FLAGS: FeatureFlagsMap = {
  [FEATURE_FLAG_KEYS.CLICK_INS]: false,
  [FEATURE_FLAG_KEYS.NON_AUTOMOTIVE_CATEGORY]: false,
};

export const featureFlagsQueryKeys = {
  all: ['featureFlags'] as const,
};

export const normalizeFeatureFlags = (raw: unknown): FeatureFlagsMap => {
  const flags: FeatureFlagsMap = { ...DEFAULT_FEATURE_FLAGS };
  if (!raw || typeof raw !== 'object') {
    return flags;
  }

  Object.entries(raw as Record<string, unknown>).forEach(([key, value]) => {
    if (typeof value === 'boolean') {
      flags[key] = value;
      return;
    }
    if (value === 0 || value === 1) {
      flags[key] = Boolean(value);
    }
  });

  return flags;
};

let cachedFlags: FeatureFlagsMap = { ...DEFAULT_FEATURE_FLAGS };

export const getCachedFeatureFlags = (): FeatureFlagsMap => cachedFlags;

export const cacheFeatureFlags = (flags: FeatureFlagsMap): FeatureFlagsMap => {
  cachedFlags = flags;
  queryClient.setQueryData(featureFlagsQueryKeys.all, flags);
  return flags;
};

export const isFeatureEnabled = (
  key: string,
  defaultValue = false
): boolean => {
  const value = cachedFlags[key];
  return value === undefined ? defaultValue : value;
};

export const isNonAutomotiveCategoryEnabled = (): boolean =>
  isFeatureEnabled(FEATURE_FLAG_KEYS.NON_AUTOMOTIVE_CATEGORY);

export const requestFeatureFlags = async (): Promise<FeatureFlagsMap> => {
  const { data } = await api.get<{
    status?: boolean;
    data?: { flags?: unknown };
  }>(`${ApiUrl.server_url}v1/feature-flags`);
  if (data.status === true && data.data?.flags != null) {
    return cacheFeatureFlags(normalizeFeatureFlags(data.data.flags));
  }
  return cacheFeatureFlags({ ...DEFAULT_FEATURE_FLAGS });
};

/** App boot fetch. Missing or failed responses leave defaults (off). */
export const fetchFeatureFlags = async (): Promise<void> => {
  try {
    await requestFeatureFlags();
  } catch (error) {
    logError('feature flags', error);
    cacheFeatureFlags({ ...DEFAULT_FEATURE_FLAGS });
  }
};
